/**
 * What the user sees when a lazy page's chunk cannot be loaded (backlog c-028).
 *
 * `lazyWithReload` (utils/lazyWithReload) reloads once on its own; an error
 * that reaches this boundary is a chunk that failed again after that reload,
 * or one that could not reload (offline, no storage). Rather than a blank app,
 * it says why and offers the reload. Offline it says to check the connection
 * instead, since "a new version" would be the wrong reason.
 *
 * Only chunk-load errors stop here; every other error is rethrown from render
 * so it reaches whatever handled it before. The prompt clears when `resetKey`
 * (the route) changes, so back and forward still work.
 */
import { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '../utils/lazyWithReload';

interface Props {
  children: ReactNode;
  /** The prompt clears when this changes (the router passes the pathname). */
  resetKey?: string;
}

interface State {
  caught: { error: unknown } | null;
}

export default class ChunkErrorBoundary extends Component<Props, State> {
  override state: State = { caught: null };

  static getDerivedStateFromError(error: unknown): State {
    return { caught: { error } };
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.caught && prev.resetKey !== this.props.resetKey) {
      this.setState({ caught: null });
    }
  }

  override render() {
    const { caught } = this.state;
    if (!caught) return this.props.children;
    if (!isChunkLoadError(caught.error)) throw caught.error;
    return reloadPrompt(navigator.onLine === false);
  }
}

function reloadPrompt(offline: boolean) {
  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center p-4" role="alert">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <i
            className={`fa-solid ${offline ? 'fa-wifi' : 'fa-arrows-rotate'} text-3xl text-primary`}
            aria-hidden="true"
          ></i>
        </div>
        <h1 className="text-2xl font-bold text-base-content mb-3">
          {offline ? 'We couldn’t load this page' : 'A new version of EmpathEase is available'}
        </h1>
        <p className="text-base-content/70 leading-relaxed mb-8">
          {offline ? 'Check your connection, then try again.' : 'Reload to get the latest version.'}
        </p>
        <button
          type="button"
          className="btn btn-primary w-full h-12 rounded-xl text-base font-medium"
          onClick={() => window.location.reload()}
        >
          {offline ? 'Try again' : 'Reload'}
        </button>
      </div>
    </div>
  );
}
